/** @jsx jsx */
import { Form, Formik } from 'formik'
import React from 'react'
import { jsx } from 'theme-ui'
import * as Yup from 'yup'
import {
  Button,
  Col,
  FormGroupInput,
  FormGroupLabel,
  LoadingPlaceholder,
  Row,
  Txt,
} from '../../components'
import { lastCardContent } from '../../components/Card'
import { ConnectedFormGroup, ConnectedFormGroupDivider } from '../../components/ConnectedFormField'
import { FormErrorMessage, GeneralFormErrorMessage } from '../../components/FormErrorMessage'
import { IClientUserData } from '../../controllers/ClientGameHandController'
import { captureExceptionWithLabel } from '../../utils/sentry'
import { IGameActions } from './types'

export const giveUserAdditionalChipsFormSchema = Yup.object().shape({
  amountGiven: Yup.number()
    .integer("If you'd like to raise blinds please select an integer between 1 - 100000")
    .min(1, "If you'd like to raise blinds please select an integer between 1 - 100000")
    .max(100000, "If you'd like to raise blinds please select an integer between 1 - 100000"),
})

export const GiveUserAdditionalChipsForm: React.SFC<{
  userData: IClientUserData
  actions: IGameActions
  onSuccess?: () => void
}> = ({ userData, actions, onSuccess }) => {
  return (
    <Formik<{ amountGiven: string }>
      initialValues={{ amountGiven: '1000' }}
      validationSchema={giveUserAdditionalChipsFormSchema}
      onSubmit={async (values, formActions) => {
        formActions.setSubmitting(true)
        try {
          await actions.giveUserAdditionalChips({
            userId: userData.id,
            chipsAmount: parseInt(values.amountGiven, 10),
          })
          if (onSuccess) {
            onSuccess()
          }
        } catch (error) {
          captureExceptionWithLabel(error, 'GiveUserAdditionalChipsForm submit')
          formActions.setFieldError(
            'generalError',
            "Whoops! Looks like we have some issues. We're working on a fix",
          )
          formActions.setSubmitting(false)
        }
      }}
    >
      {(formProps) => {
        return (
          <Form sx={{ maxWidth: 600, mx: 'auto' }}>
            <Row sx={{ alignItems: 'center' }}>
              <Col xs={12} md={8}>
                <ConnectedFormGroup name={'amountGiven'}>
                  {({ field, hasErrors }) => (
                    <React.Fragment>
                      <FormGroupLabel htmlFor={field.name}>Chips given</FormGroupLabel>
                      <ConnectedFormGroupDivider />
                      <FormGroupInput
                        type="number"
                        hasErrors={hasErrors}
                        id={field.name}
                        sx={lastCardContent}
                        {...field}
                      />
                    </React.Fragment>
                  )}
                </ConnectedFormGroup>
              </Col>
              <Col xs={12} md={4}>
                <Txt sx={{ textAlign: 'center', mt: [3, null, null, 0] }}>
                  <Button type="submit" size="lg" block disabled={formProps.isSubmitting}>
                    <LoadingPlaceholder loadingVariant="light" loading={formProps.isSubmitting}>
                      Submit
                    </LoadingPlaceholder>
                  </Button>
                </Txt>
              </Col>
            </Row>
            <FormErrorMessage name={'amountGiven'} />
            <GeneralFormErrorMessage sx={{ mt: 3 }} />
          </Form>
        )
      }}
    </Formik>
  )
}
